import Stack from "@mui/material/Stack";
import CustomTabPanel from "components/customTabPanel/CustomTabPanel";
import EmptyData from "components/emptyData/EmptyData";
import { AppAction, AppContext } from "helpers/context";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { categorizeAddressData } from "./address-data-categorize";
import AddressEncodingPanel from "./address-encoding-panel/address-encoding-panel";
import AddressEncodingTabs from "./address-encoding-tabs/address-encoding-tabs";

interface AddressEncodingContentProps {
  data: any[];
  candidatesData: any[];
  setCandidatesData: (data: any[]) => void;
  noMatchData: any[];
  setNoMatchData: (data: any[]) => void;
  matchData: any[];
  setMatchData: (data: any[]) => void;
}

function AddressEncodingContent({
  data,
  candidatesData,
  setCandidatesData,
  noMatchData,
  setNoMatchData,
  matchData,
  setMatchData
}: AddressEncodingContentProps) {
  const [context, dispatch] = useContext(AppContext);

  const [t] = useTranslation();

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const moveItem = useCallback(
    (
      id: string,
      from: any[],
      setFrom: Function,
      to: any[] | null,
      setTo: Function | null
    ) => {
      const itemToMove = from.find(item => item.id === id);
      if (!itemToMove) return;

      setFrom(from.filter(item => item.id !== id));
      if (to && setTo) {
        setTo([...to, itemToMove]);
      }
    },
    []
  );

  const handleMove = useCallback(
    (id: string) => {
      moveItem(id, matchData, setMatchData, noMatchData, setNoMatchData);
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: { title: "moveMatchTitle", titleIconColor: "yellow" }
      });
    },
    [matchData, noMatchData, moveItem, dispatch]
  );

  const handleNoMatch = useCallback(
    (id: string, type?: string) => {
      if (type === "complete") {
        moveItem(id, noMatchData, setNoMatchData, matchData, setMatchData);
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: { title: "moveNoMatchCompleteTitle" }
        });
      } else {
        // delete
        moveItem(id, noMatchData, setNoMatchData, null, null);
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: { title: "moveSkipTitle", titleIconColor: "red" }
        });
      }
    },
    [noMatchData, matchData, moveItem, dispatch]
  );

  const handleSelect = useCallback(
    (id: string, type?: string) => {
      if (type === "skip") {
        moveItem(
          id,
          candidatesData,
          setCandidatesData,
          noMatchData,
          setNoMatchData
        );
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: { title: "moveSkipTitle" }
        });
      } else {
        moveItem(
          id,
          candidatesData,
          setCandidatesData,
          matchData,
          setMatchData
        );
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: { title: "moveCandidateTitle" }
        });
      }
    },
    [candidatesData, noMatchData, matchData, moveItem, dispatch]
  );

  const setCandidatesDataCallback = useCallback(
    (data: any[]) => setCandidatesData(data),
    [setCandidatesData]
  );
  const setNoMatchDataCallback = useCallback(
    (data: any[]) => setNoMatchData(data),
    [setNoMatchData]
  );
  const setMatchDataCallback = useCallback(
    (data: any[]) => setMatchData(data),
    [setMatchData]
  );

  const init = useCallback(
    () => {
      try {
        if (!data) {
          return;
        }

        const importMultipleLocations = context.importMultipleLocations;

        if (!importMultipleLocations) {
          return;
        }

        const items = importMultipleLocations.items;

        if (!items || items.length === 0) {
          return;
        }

        const { candidates, noMatch, match } = categorizeAddressData(
          items,
          data
        );

        setCandidatesData(candidates);
        setNoMatchData(noMatch);
        setMatchData(match);

        // dispatch({ type: AppAction.setAddressEncodingCandidatesData, payload: candidates });
        // dispatch({ type: AppAction.setAddressEncodingNoMatchData, payload: noMatch });
        // dispatch({ type: AppAction.setAddressEncodingMatchData, payload: match });
      } catch (error) {
        console.error("分类数据失败:", error);
      } finally {
        dispatch({ type: AppAction.setAddressEncodingLoading, payload: false });
      }
    },
    [
      data,
      context.importMultipleLocations,
      dispatch,
      setCandidatesData,
      setNoMatchData,
      setMatchData
    ]
  );

  useEffect(
    () => {
      init();
    },
    [init]
  );

  return (
    <Stack
      sx={{
        height: "100%",
        // border: "1px solid red",
        borderRadius: "3px",
        // padding: "10px",
        // display: "flex",
        // flexDirection: "column"
      }}
    >
      <AddressEncodingTabs
        value={value}
        handleChange={handleChange}
        candidatesDataLength={candidatesData.length}
        noMatchDataLength={noMatchData.length}
        matchDataLength={matchData.length}
      />
      <CustomTabPanel value={value} index={0}>
        {!candidatesData || candidatesData.length === 0
          ? <EmptyData />
          : <AddressEncodingPanel
              data={candidatesData}
              handleMove={handleSelect}
              handleData={setCandidatesDataCallback}
              title={t("addressEncoding.text.candidates")}
              tooltip={t("addressEncoding.hint.candidates")}
              type="candidates"
            />}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        {!noMatchData || noMatchData.length === 0
          ? <EmptyData />
          : <AddressEncodingPanel
              data={noMatchData}
              type="no_match"
              handleMove={handleNoMatch}
              handleData={setNoMatchDataCallback}
              isShowBtn={true}
              title={t("addressEncoding.text.no_match")}
              tooltip={t("addressEncoding.hint.no_match")}
            />}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        {!matchData || matchData.length === 0
          ? <EmptyData />
          : <AddressEncodingPanel
              data={matchData}
              type="match"
              handleMove={handleMove}
              handleData={setMatchDataCallback}
              isShowBtn={true}
              title={t("addressEncoding.text.match")}
              tooltip={t("addressEncoding.hint.match")}
            />}
      </CustomTabPanel>
    </Stack>
  );
}

export default AddressEncodingContent;
